import React, { useState, useEffect } from "react";
import { useBusinessPlan } from "../App";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { TooltipLabel } from "./ui/TooltipLabel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { 
  Target, Plus, Edit3, Trash2, ChevronRight, ChevronDown, 
  TrendingUp, DollarSign, Users, Calendar, Building2, ArrowRight,
  Lightbulb, Zap, Package, BarChart3, Eye, EyeOff, HelpCircle
} from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, Legend, Area, AreaChart, PieChart, Pie, Cell } from "recharts";
import { strategicFrameworkTooltips } from "../utils/strategicFrameworkTooltips";
import { useCurrency } from "../contexts/CurrencyContext";
import { getCurrencySymbol, CURRENCIES } from "../constants/currencies";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StrategicFramework = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [framework, setFramework] = useState(null);
  
  // Format currency with automatic conversion
  const formatCurrency = (amount, currency = null) => {
    if (!amount) return formatCurrencyContext(0, currentPlan?.currency || 'USD', null, false);
    const sourceCurrency = currency || currentPlan?.currency || 'USD';
    return formatCurrencyContext(amount, sourceCurrency, null, false);
  };
  const [products, setProducts] = useState([]);
  const [standardPillars, setStandardPillars] = useState([]);
  const [standardStatuses, setStandardStatuses] = useState([]);
  const [standardStreamTypes, setStandardStreamTypes] = useState([]);
  const [standardProductCategories, setStandardProductCategories] = useState([]);
  const [standardServiceCategories, setStandardServiceCategories] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("objectives");
  
  // Cost Analysis states  
  const [costAnalysis, setCostAnalysis] = useState(null);
  const [roiAnalysis, setRoiAnalysis] = useState(null);
  const [costAnalysisLoading, setCostAnalysisLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  // Dialog states
  const [showObjectiveDialog, setShowObjectiveDialog] = useState(false);
  const [showInitiativeDialog, setShowInitiativeDialog] = useState(false);
  const [showStreamDialog, setShowStreamDialog] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [selectedInitiative, setSelectedInitiative] = useState(null);
  const [selectedRevenueStream, setSelectedRevenueStream] = useState(null);

  // Form states
  const [objectiveForm, setObjectiveForm] = useState({
    objective_id: "",
    pillar: "",
    objective_name: "",
    description: "",
    owner: "",
    ownerCustom: false,
    start_date: "",
    end_date: "",
    status: "Planning",
    createInitiative: false,
    initiatives: [{ name: "", description: "" }]
  });

  const [initiativeForm, setInitiativeForm] = useState({
    initiative_id: "",
    linked_objective_id: "",
    initiative_name: "",
    description: "",
    budget: "",
    timeline_start: "",
    timeline_end: "",
    owner: "",
    ownerCustom: false,
    status: "Planning",
    createRevenueStream: false,
    revenueStreams: [{ name: "", type: "Hybrid Revenue" }]
  });

  const [streamForm, setStreamForm] = useState({
    stream_id: "",
    linked_objective_id: "",
    linked_initiative_id: "",
    stream_name: "",
    stream_type: "Hybrid",
    description: "",
    notes: "",
    owner: "",
    ownerCustom: false
  });

  const [productForm, setProductForm] = useState({
    sku: "",
    name: "",
    description: "",
    category: "",
    type: "products",
    base_price: "",
    currency: currentPlan?.currency || 'USD',  // Currency for base_price
    cogs_percentage: "",
    annual_sales_volume_growth: "",
    growth_rates_per_year: {},  // {2025: 15, 2026: 20, 2027: 12, ...}
    use_per_year_rates: false,   // Toggle between single rate and per-year rates
    base_year_units: "",
    active: true,
    launch_year: "",
    end_year: "",
    linked_revenue_stream_id: "",
    owner: "",
    ownerCustom: false
  });

  useEffect(() => {
    if (currentPlan) {
      fetchStrategicFramework();
      fetchProducts();
      fetchStandardCategories();
      fetchTeamMembers();
    }
  }, [currentPlan]);

  // Load cost analysis when the cost-analysis tab becomes active
  useEffect(() => {
    if (currentPlan && activeTab === "cost-analysis") {
      fetchCostAnalysis();
    }
  }, [currentPlan, activeTab]);

  const fetchStandardCategories = async () => {
    try {
      const [pillarsRes, statusesRes, streamTypesRes, productCatRes, serviceCatRes] = await Promise.all([
        axios.get(`${API}/categories/strategic_pillars/${currentPlan.id}`),
        axios.get(`${API}/categories/strategic_statuses/${currentPlan.id}`),
        axios.get(`${API}/categories/revenue_stream_types/${currentPlan.id}`),
        axios.get(`${API}/categories/product_categories/${currentPlan.id}`),
        axios.get(`${API}/categories/service_categories/${currentPlan.id}`)
      ]);
      
      setStandardPillars(pillarsRes.data?.all || []);
      setStandardStatuses(statusesRes.data?.all || []);
      setStandardStreamTypes(streamTypesRes.data?.all || []);
      setStandardProductCategories(productCatRes.data?.all || []);
      setStandardServiceCategories(serviceCatRes.data?.all || []);
    } catch (error) {
      console.error("Error fetching standard categories:", error);
      // Set fallback values
      setStandardPillars(["Financial Performance", "Customer & Stakeholder", "Internal Processes"]);
      setStandardStatuses(["Planning", "In Progress", "Completed", "On Hold"]);
      setStandardStreamTypes(["Product Revenue", "Service Revenue", "Hybrid Revenue"]);
      setStandardProductCategories(["Software Products", "Hardware Products", "Digital Products"]);
      setStandardServiceCategories(["Professional Services", "Consulting Services", "Support Services"]);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/enterprise/team-members?plan_id=${currentPlan.id}`);
      const members = response.data?.team_members || [];
      // Deduplicate team members by both ID and name to avoid duplicate warnings
      const uniqueMembers = members.reduce((acc, member) => {
        // Skip if we already have a member with same ID or same name
        if (!acc.find(m => m.id === member.id || m.name === member.name)) {
          acc.push(member);
        }
        return acc;
      }, []);
      setTeamMembers(uniqueMembers);
    } catch (error) {
      console.error("Error fetching team members:", error);
      setTeamMembers([]);
    }
  };

  const fetchStrategicFramework = async () => {
    try {
      const response = await axios.get(`${API}/v2/strategic-framework/${currentPlan.id}`);
      setFramework(response.data);
    } catch (error) {
      console.error("Error fetching strategic framework:", error);
      setFramework({ objectives: [], total_objectives: 0, total_initiatives: 0, total_revenue_streams: 0, total_strategic_products: 0 });
    }
  };

  const generateNextId = (type, existingItems = []) => {
    const prefixMap = {
      'objective': 'OBJ',
      'initiative': 'INI', 
      'stream': 'REV',
      'product': 'PRD',
      'service': 'SRV'
    };
    
    const prefix = prefixMap[type] || 'GEN';
    const existingNumbers = existingItems
      .map(item => {
        const id = item.objective_id || item.initiative_id || item.stream_id || item.sku || '';
        const match = id.match(new RegExp(`${prefix}-(\\d+)`));
        return match ? parseInt(match[1]) : 0;
      })
      .filter(num => !isNaN(num));
    
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    return `${prefix}-${nextNumber.toString().padStart(3, '0')}`;
  };

  // Helper function to calculate projected units with per-year or single growth rate
  const calculateProjectedUnits = (product, targetYear) => {
    const baseUnits = product.base_year_units || 0;
    const baseYear = 2025;
    
    // If using per-year rates
    if (product.growth_rates_per_year && Object.keys(product.growth_rates_per_year).length > 0) {
      let units = baseUnits;
      for (let year = baseYear + 1; year <= targetYear; year++) {
        const yearRate = product.growth_rates_per_year[year.toString()] || 0;
        units = units * (1 + yearRate / 100);
      }
      return units;
    }
    
    // Otherwise use single rate
    const yearsFromBase = targetYear - baseYear;
    const growthRate = product.annual_sales_volume_growth || 0;
    return baseUnits * Math.pow(1 + (growthRate / 100), yearsFromBase);
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/v2/products-enhanced/${currentPlan.id}?include_strategic_only=true`);
      setProducts(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
      setLoading(false);
    }
  };

  const fetchCostAnalysis = async () => {
    if (!currentPlan) return;
    
    setCostAnalysisLoading(true);
    try {
      const [costResponse, roiResponse] = await Promise.all([
        axios.get(`${API}/v3/calculations/cost-summary/${currentPlan.id}`),
        axios.get(`${API}/v3/calculations/roi-analysis/${currentPlan.id}`)
      ]);
      
      setCostAnalysis(costResponse.data);
      setRoiAnalysis(roiResponse.data);
    } catch (error) {
      console.error("Error fetching cost analysis:", error);
      toast.error("Failed to fetch cost analysis data");
      setCostAnalysis(null);
      setRoiAnalysis(null);
    } finally {
      setCostAnalysisLoading(false);
    }
  };

  const handleToggleExpanded = (type, id) => {
    const key = `${type}_${id}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleEditObjective = (objective) => {
    setEditingItem(objective);
    setObjectiveForm({
      objective_id: objective.objective_id || "",
      pillar: objective.pillar || "",
      objective_name: objective.objective_name || "",
      description: objective.description || "",
      owner: objective.owner || "",
      ownerCustom: false,
      start_date: objective.start_date ? objective.start_date.split('T')[0] : "",
      end_date: objective.end_date ? objective.end_date.split('T')[0] : "",
      status: objective.status || "Planning",
      createInitiative: false,
      initiatives: [{ name: "", description: "" }]
    });
    setShowObjectiveDialog(true);
  };

  const handleEditInitiative = (initiative) => {
    setEditingItem(initiative);
    setInitiativeForm({
      initiative_id: initiative.initiative_id || "",
      linked_objective_id: initiative.linked_objective_id || "",
      initiative_name: initiative.initiative_name || "",
      description: initiative.description || "",
      budget: initiative.budget?.toString() || "",
      timeline_start: initiative.timeline_start ? initiative.timeline_start.split('T')[0] : "",
      timeline_end: initiative.timeline_end ? initiative.timeline_end.split('T')[0] : "",
      owner: initiative.owner || "",
      ownerCustom: false,
      status: initiative.status || "Planning",
      createRevenueStream: false,
      revenueStreams: [{ name: "", type: "Hybrid Revenue" }]
    });
    setShowInitiativeDialog(true);
  };

  const handleEditStream = (stream) => {
    setEditingItem(stream);
    setStreamForm({
      stream_id: stream.stream_id || "",
      linked_objective_id: stream.objective?.id || "",
      linked_initiative_id: stream.initiative?.id || stream.linked_initiative_id || "",
      stream_name: stream.stream_name || "",
      stream_type: stream.stream_type || "Hybrid",
      description: stream.description || "",
      notes: stream.notes || "",
      owner: stream.owner || "",
      ownerCustom: false
    });
    setShowStreamDialog(true);
  };

  // Delete handlers
  const handleDeleteObjective = async (objectiveId) => {
    if (!window.confirm('Are you sure you want to delete this objective? This will also remove all linked initiatives and revenue streams.')) {
      return;
    }

    try {
      await axios.delete(`${API}/v2/strategic-objectives/${objectiveId}`);
      toast.success('Objective deleted successfully');
      fetchStrategicFramework(); // Refresh to update linked data
    } catch (error) {
      console.error('Error deleting objective:', error);
      toast.error('Failed to delete objective');
    }
  };

  const handleDeleteInitiative = async (initiativeId) => {
    if (!window.confirm('Are you sure you want to delete this initiative? This will also remove all linked revenue streams.')) {
      return;
    }

    try {
      await axios.delete(`${API}/v2/strategic-initiatives/${initiativeId}`);
      toast.success('Initiative deleted successfully');
      fetchStrategicFramework(); // Refresh to update linked data
    } catch (error) {
      console.error('Error deleting initiative:', error);
      toast.error('Failed to delete initiative');
    }
  };

  const handleDeleteStream = async (streamId) => {
    if (!window.confirm('Are you sure you want to delete this revenue stream? This will also unlink all products.')) {
      return;
    }

    try {
      await axios.delete(`${API}/v2/revenue-streams/${streamId}`);
      toast.success('Revenue stream deleted successfully');
      fetchStrategicFramework(); // Refresh to update linked data
    } catch (error) {
      console.error('Error deleting revenue stream:', error);
      toast.error('Failed to delete revenue stream');
    }
  };

  const resetObjectiveForm = () => {
    const nextId = generateNextId('objective', framework?.objectives || []);
    setObjectiveForm({
      objective_id: nextId,
      pillar: "",
      objective_name: "",
      description: "",
      owner: "",
      ownerCustom: false,
      start_date: "",
      end_date: "",
      status: "Planning",
      createInitiative: false,
      initiatives: [{ name: "", description: "" }]
    });
    setEditingItem(null);
  };

  const resetInitiativeForm = () => {
    const allInitiatives = [];
    framework?.objectives?.forEach(obj => {
      obj.initiatives?.forEach(init => allInitiatives.push(init));
    });
    const nextId = generateNextId('initiative', allInitiatives);
    
    setInitiativeForm({
      initiative_id: nextId,
      linked_objective_id: selectedObjective?.id || "",
      initiative_name: "",
      description: "",
      budget: "",
      timeline_start: "",
      timeline_end: "",
      owner: "",
      ownerCustom: false,
      status: "Planning",
      createRevenueStream: false,
      revenueStreams: [{ name: "", type: "Hybrid Revenue" }]
    });
    setEditingItem(null);
  };

  const resetStreamForm = () => {
    const allStreams = [];
    framework?.objectives?.forEach(obj => {
      obj.initiatives?.forEach(init => {
        init.revenue_streams?.forEach(stream => allStreams.push(stream));
      });
    });
    const nextId = generateNextId('stream', allStreams);
    
    setStreamForm({
      stream_id: nextId,
      linked_objective_id: "",
      linked_initiative_id: selectedInitiative?.id || "",
      stream_name: "",
      stream_type: "Hybrid",
      description: "",
      notes: "",
      owner: "",
      ownerCustom: false
    });
    setEditingItem(null);
  };

  const resetProductForm = () => {
    const nextId = generateNextId(productForm.type === 'services' ? 'service' : 'product', products);
    setProductForm({
      sku: nextId,
      name: "",
      description: "",
      category: "",
      type: "products",
      base_price: "",
      currency: currentPlan?.currency || 'USD',  // Currency for base_price
      cogs_percentage: "",
      annual_sales_volume_growth: "",
      growth_rates_per_year: {},
      use_per_year_rates: false,
      base_year_units: "",
      active: true,
      launch_year: "",
      end_year: "",
      linked_revenue_stream_id: selectedRevenueStream?.id || "",
      owner: "",
      ownerCustom: false
    });
    setEditingItem(null);
  };

  const handleCreateObjective = async (e) => {
    e.preventDefault();
    
    if (!objectiveForm.objective_id.trim() || !objectiveForm.objective_name.trim() || !objectiveForm.pillar) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const objectiveData = {
        plan_id: currentPlan.id,
        objective_id: objectiveForm.objective_id.trim(),
        pillar: objectiveForm.pillar,
        objective_name: objectiveForm.objective_name.trim(),
        description: objectiveForm.description.trim() || "",
        owner: objectiveForm.owner.trim() || null,
        start_date: objectiveForm.start_date ? new Date(objectiveForm.start_date).toISOString() : null,
        end_date: objectiveForm.end_date ? new Date(objectiveForm.end_date).toISOString() : null,
        status: objectiveForm.status || "Planning"
      };

      let objectiveResponse;
      if (editingItem) {
        objectiveResponse = await axios.put(`${API}/v2/strategic-objectives/${editingItem.id}`, objectiveData);
        toast.success("Strategic objective updated successfully!");
      } else {
        objectiveResponse = await axios.post(`${API}/v2/strategic-objectives`, objectiveData);
        toast.success("Strategic objective created successfully!");
        
        // If user wants to create initiatives immediately
        if (objectiveForm.createInitiative && objectiveForm.initiatives && objectiveForm.initiatives.length > 0) {
          for (const [index, initiative] of objectiveForm.initiatives.entries()) {
            if (initiative.name.trim()) {
              const initiativeData = {
                plan_id: currentPlan.id,
                initiative_id: `INI-${Date.now().toString().slice(-6)}-${index + 1}`,
                linked_objective_id: objectiveResponse.data.id,
                initiative_name: initiative.name.trim(),
                description: initiative.description.trim() || `Initiative under ${objectiveForm.objective_name}`,
                status: "Planning"
              };
              
              await axios.post(`${API}/v2/strategic-initiatives`, initiativeData);
            }
          }
          toast.success(`${objectiveForm.initiatives.filter(i => i.name.trim()).length} initiatives created!`);
        }
      }

      fetchStrategicFramework();
      setShowObjectiveDialog(false);
      resetObjectiveForm();
    } catch (error) {
      console.error("Error saving objective:", error);
      toast.error(error.response?.data?.detail || "Failed to save strategic objective");
    }
  };

  const handleCreateInitiative = async (e) => {
    e.preventDefault();
    
    if (!initiativeForm.initiative_id.trim() || !initiativeForm.initiative_name.trim() || !initiativeForm.linked_objective_id) {
      toast.error("Please fill in all required fields: Initiative ID, Name, and Linked Objective");
      return;
    }

    try {
      const initiativeData = {
        plan_id: currentPlan.id,
        initiative_id: initiativeForm.initiative_id.trim(),
        linked_objective_id: initiativeForm.linked_objective_id,
        initiative_name: initiativeForm.initiative_name.trim(),
        description: initiativeForm.description.trim() || "",
        budget: initiativeForm.budget ? parseFloat(initiativeForm.budget) : null,
        timeline_start: initiativeForm.timeline_start ? new Date(initiativeForm.timeline_start).toISOString() : null,
        timeline_end: initiativeForm.timeline_end ? new Date(initiativeForm.timeline_end).toISOString() : null,
        owner: initiativeForm.owner.trim() || null,
        status: initiativeForm.status || "Planning"
      };

      console.log("Creating/updating initiative with data:", initiativeData);

      let initiativeResponse;
      if (editingItem) {
        initiativeResponse = await axios.put(`${API}/v2/strategic-initiatives/${editingItem.id}`, initiativeData);
        toast.success("Strategic initiative updated successfully!");
      } else {
        initiativeResponse = await axios.post(`${API}/v2/strategic-initiatives`, initiativeData);
        toast.success("Strategic initiative created successfully!");
        
        // If user wants to create revenue streams
        if (initiativeForm.createRevenueStream && initiativeForm.revenueStreams && initiativeForm.revenueStreams.length > 0) {
          for (const streamData of initiativeForm.revenueStreams) {
            if (streamData.name.trim()) {
              const newStreamData = {
                plan_id: currentPlan.id,
                stream_id: `REV-${Date.now().toString().slice(-6)}-${Math.random().toString(36).substr(2, 3)}`,
                linked_initiative_id: initiativeResponse.data.id,
                stream_name: streamData.name.trim(),
                stream_type: streamData.type || "Hybrid Revenue",
                description: `Revenue stream under ${initiativeForm.initiative_name}`
              };
              
              await axios.post(`${API}/v2/revenue-streams`, newStreamData);
            }
          }
          toast.success(`${initiativeForm.revenueStreams.filter(s => s.name.trim()).length} revenue streams created!`);
        }
      }

      fetchStrategicFramework();
      setShowInitiativeDialog(false);
      resetInitiativeForm();
    } catch (error) {
      console.error("Error saving initiative:", error);
      toast.error(error.response?.data?.detail || "Failed to save strategic initiative");
    }
  };

  const handleCreateStream = async (e) => {
    e.preventDefault();
    
    if (!streamForm.stream_id.trim() || !streamForm.stream_name.trim() || !streamForm.linked_initiative_id) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const streamData = {
        plan_id: currentPlan.id,
        stream_id: streamForm.stream_id.trim(),
        linked_initiative_id: streamForm.linked_initiative_id,
        stream_name: streamForm.stream_name.trim(),
        stream_type: streamForm.stream_type,
        description: streamForm.description.trim() || "",
        notes: streamForm.notes.trim() || ""
      };

      if (editingItem) {
        await axios.put(`${API}/v2/revenue-streams/${editingItem.id}`, streamData);
        toast.success("Revenue stream updated successfully!");
      } else {
        await axios.post(`${API}/v2/revenue-streams`, streamData);
        toast.success("Revenue stream created successfully!");
      }

      fetchStrategicFramework();
      setShowStreamDialog(false);
      resetStreamForm();
    } catch (error) {
      console.error("Error saving stream:", error);
      toast.error(error.response?.data?.detail || "Failed to save revenue stream");
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    
    if (!productForm.sku.trim() || !productForm.name.trim() || !productForm.base_price || !productForm.linked_revenue_stream_id) {
      toast.error("Please fill in all required fields and select a revenue stream");
      return;
    }

    try {
      const productData = {
        ...productForm,
        plan_id: currentPlan.id,
        base_price: parseFloat(productForm.base_price),
        cogs_percentage: parseFloat(productForm.cogs_percentage) || 0,
        annual_sales_volume_growth: productForm.annual_sales_volume_growth ? parseFloat(productForm.annual_sales_volume_growth) : null,
        base_year_units: productForm.base_year_units ? parseInt(productForm.base_year_units) : null,
        launch_year: productForm.launch_year ? parseInt(productForm.launch_year) : null,
        end_year: productForm.end_year ? parseInt(productForm.end_year) : null,
        is_strategic_linked: true
      };

      if (editingItem) {
        await axios.put(`${API}/v2/products-enhanced/${editingItem.id}`, productData);
        toast.success("Product updated successfully!");
      } else {
        await axios.post(`${API}/v2/products-enhanced`, productData);
        toast.success("Product created successfully!");
      }

      fetchProducts();
      fetchStrategicFramework();
      setShowProductDialog(false);
      resetProductForm();
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
  };

  const handleEditProduct = (product) => {
    setEditingItem(product);
    setProductForm({
      sku: product.sku,
      name: product.name,
      description: product.description,
      category: product.category || "",
      type: product.type || "products",
      base_price: product.base_price?.toString() || "",
      currency: product.currency || currentPlan?.currency || 'USD',  // Use product's currency or plan's currency
      cogs_percentage: product.cogs_percentage?.toString() || "",
      annual_sales_volume_growth: product.annual_sales_volume_growth?.toString() || "",
      growth_rates_per_year: product.growth_rates_per_year || {},
      use_per_year_rates: product.growth_rates_per_year && Object.keys(product.growth_rates_per_year).length > 0,
      base_year_units: product.base_year_units?.toString() || "",
      active: product.active,
      launch_year: product.launch_year?.toString() || "",
      end_year: product.end_year?.toString() || "",
      linked_revenue_stream_id: product.linked_revenue_stream_id || "",
      owner: product.owner || "",
      ownerCustom: false
    });
    setShowProductDialog(true);
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Product deleted successfully");
      fetchProducts();
      fetchStrategicFramework();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      "Planning": "bg-blue-100 text-blue-700",
      "In Progress": "bg-yellow-100 text-yellow-700",
      "Completed": "bg-green-100 text-green-700",
      "On Hold": "bg-gray-100 text-gray-700",
      "Cancelled": "bg-red-100 text-red-700",
      "Under Review": "bg-purple-100 text-purple-700"
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  const getPillarIcon = (pillar) => {
    const icons = {
      "Financial Performance": DollarSign,
      "Customer & Stakeholder": Users,
      "Internal Processes": BarChart3,
      "People & Culture": Users,
      "Innovation & Growth": Lightbulb,
      "Quality & Excellence": Target,
      "Risk Management": Building2,
      "Sustainability & ESG": TrendingUp,
      "Market Position": Zap,
      "Operational Efficiency": Package
    };
    return icons[pillar] || Target;
  };

  const getAllRevenueStreams = () => {
    const streams = [];
    if (framework?.objectives) {
      framework.objectives.forEach(obj => {
        obj.initiatives?.forEach(init => {
          init.revenue_streams?.forEach(stream => {
            streams.push({
              id: stream.id,
              label: `${stream.stream_name} (${init.initiative_name})`,
              stream: stream
            });
          });
        });
      });
    }
    return streams;
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 pt-16 lg:pt-0">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-3/4 md:w-1/2"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-48 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6 md:space-y-8 fade-in pt-16 lg:pt-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <TooltipLabel 
            htmlFor="strategic-framework-title"
            tooltip={{
              title: "Strategic Framework",
              content: "A comprehensive system for planning and tracking your business strategy from high-level objectives down to individual product sales and costs.",
              example: "Build a clear hierarchy: Strategic Goals → Action Plans → Revenue Sources → Products → Sales Performance"
            }}
            className="text-3xl font-bold text-slate-900 mb-2"
          >
            Strategic Framework
          </TooltipLabel>
          <p className="text-slate-600">
            Objectives → Initiatives → Revenue Streams → Products → Sales for {currentPlan?.company_name}
          </p>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="objectives" className="flex items-center space-x-2">
            <Target className="h-4 w-4" />
            <span>Objectives</span>
          </TabsTrigger>
          <TabsTrigger value="initiatives" className="flex items-center space-x-2">
            <Lightbulb className="h-4 w-4" />
            <span>Initiatives</span>
          </TabsTrigger>
          <TabsTrigger value="revenue-streams" className="flex items-center space-x-2">
            <DollarSign className="h-4 w-4" />
            <span>Revenue Streams</span>
          </TabsTrigger>
          <TabsTrigger value="products" className="flex items-center space-x-2">
            <Package className="h-4 w-4" />
            <span>Products</span>
          </TabsTrigger>
          <TabsTrigger value="sales" className="flex items-center space-x-2">
            <TrendingUp className="h-4 w-4" />
            <span>Sales</span>
          </TabsTrigger>
        </TabsList>

        {/* Objectives Tab */}
        <TabsContent value="objectives" className="space-y-6">
          {/* Objectives Header */}
          <div className="flex items-center justify-between">
            <div>
              <TooltipLabel 
                htmlFor="objectives-header" 
                tooltip={strategicFrameworkTooltips.sections.objectives}
                className="text-2xl font-bold text-slate-900 mb-2"
              >
                Strategic Objectives
              </TooltipLabel>
              <p className="text-slate-600">Define high-level strategic goals for {currentPlan?.company_name}</p>
            </div>
            <Dialog open={showObjectiveDialog} onOpenChange={setShowObjectiveDialog}>
              <DialogTrigger asChild>
                <Button onClick={resetObjectiveForm} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Objective
                </Button>
              </DialogTrigger>
            </Dialog>
          </div>

          {/* Objectives Grid */}
          {framework?.objectives && framework.objectives.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {framework.objectives.map((objective) => {
                const PillarIcon = getPillarIcon(objective.pillar);
                return (
                  <Card key={objective.id} className="glass-card shadow-soft">
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <PillarIcon className="h-5 w-5 mr-2 text-blue-600" />
                            <CardTitle className="text-lg mr-2">{objective.objective_name}</CardTitle>
                            <Badge className={getStatusColor(objective.status)}>
                              {objective.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-blue-600 font-medium mb-1">{objective.objective_id} • {objective.pillar}</p>
                          <CardDescription>{objective.description}</CardDescription>
                          {objective.owner && (
                            <p className="text-xs text-slate-500 mt-1">Owner: {objective.owner}</p>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-xs">
                          {objective.initiatives?.length || 0} Initiative{(objective.initiatives?.length || 0) !== 1 ? 's' : ''}
                        </Badge>
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditObjective(objective)}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteObjective(objective.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="glass-card shadow-soft">
              <CardContent className="p-12 text-center">
                <Target className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Strategic Objectives Yet</h3>
                <p className="text-slate-600 mb-6">
                  Start by creating your first strategic objective to begin building your strategic framework.
                </p>
                <Button onClick={() => {setShowObjectiveDialog(true); resetObjectiveForm();}} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Objective
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Initiatives Tab */}
        <TabsContent value="initiatives" className="space-y-6">
          {/* Initiatives Header */}
          <div className="flex items-center justify-between">
            <div>
              <TooltipLabel 
                htmlFor="initiatives-header" 
                tooltip={strategicFrameworkTooltips.sections.initiatives}
                className="text-2xl font-bold text-slate-900 mb-2"
              >
                Strategic Initiatives
              </TooltipLabel>
              <p className="text-slate-600">Programs and projects that deliver on strategic objectives</p>
            </div>
            <Dialog open={showInitiativeDialog} onOpenChange={setShowInitiativeDialog}>
              <DialogTrigger asChild>
                <Button 
                  onClick={resetInitiativeForm} 
                  className="btn-primary"
                  disabled={!framework?.objectives || framework.objectives.length === 0}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Initiative
                </Button>
              </DialogTrigger>
            </Dialog>
          </div>

          {framework?.objectives && framework.objectives.length === 0 && (
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <Target className="h-6 w-6 text-yellow-600" />
                  <div>
                    <h3 className="font-medium text-yellow-800">Create Objectives First</h3>
                    <p className="text-yellow-700 text-sm">
                      You need to create strategic objectives before adding initiatives.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Initiatives Grid */}
          {(() => {
            const allInitiatives = [];
            framework?.objectives?.forEach(obj => {
              obj.initiatives?.forEach(init => {
                allInitiatives.push({...init, objective: obj});
              });
            });
            
            return allInitiatives.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allInitiatives.map((initiative) => (
                  <Card key={initiative.id} className="glass-card shadow-soft">
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <Lightbulb className="h-5 w-5 mr-2 text-green-600" />
                            <CardTitle className="text-lg mr-2">{initiative.initiative_name}</CardTitle>
                            <Badge className={getStatusColor(initiative.status)}>
                              {initiative.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-green-600 font-medium mb-1">{initiative.initiative_id}</p>
                          <p className="text-xs text-blue-600 mb-2">→ {initiative.objective.objective_name}</p>
                          <CardDescription>{initiative.description}</CardDescription>
                          {initiative.budget && (
                            <p className="text-xs text-slate-500 mt-1">Budget: {formatCurrencyContext(initiative.budget)}</p>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-xs">
                          {initiative.revenue_streams?.length || 0} Stream{(initiative.revenue_streams?.length || 0) !== 1 ? 's' : ''}
                        </Badge>
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditInitiative(initiative)}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteInitiative(initiative.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : framework?.objectives?.length > 0 ? (
              <Card className="glass-card shadow-soft">
                <CardContent className="p-12 text-center">
                  <Lightbulb className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No Initiatives Yet</h3>
                  <p className="text-slate-600 mb-6">
                    Create initiatives to execute your strategic objectives.
                  </p>
                  <Button onClick={() => {setShowInitiativeDialog(true); resetInitiativeForm();}} className="btn-primary">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Initiative
                  </Button>
                </CardContent>
              </Card>
            ) : null;
          })()}
        </TabsContent>

        {/* Revenue Streams Tab */}
        <TabsContent value="revenue-streams" className="space-y-6">
          {/* Revenue Streams Header */}
          <div className="flex items-center justify-between">
            <div>
              <TooltipLabel 
                htmlFor="revenue-streams-header" 
                tooltip={strategicFrameworkTooltips.sections.revenueStreams}
                className="text-2xl font-bold text-slate-900 mb-2"
              >
                Revenue Streams
              </TooltipLabel>
              <p className="text-slate-600">Business lines and revenue sources from initiatives</p>
            </div>
            <Dialog open={showStreamDialog} onOpenChange={setShowStreamDialog}>
              <DialogTrigger asChild>
                <Button 
                  onClick={resetStreamForm} 
                  className="btn-primary"
                  disabled={(() => {
                    const hasInitiatives = framework?.objectives?.some(obj => obj.initiatives && obj.initiatives.length > 0);
                    return !hasInitiatives;
                  })()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Revenue Stream
                </Button>
              </DialogTrigger>
            </Dialog>
          </div>

          {(() => {
            const hasInitiatives = framework?.objectives?.some(obj => obj.initiatives && obj.initiatives.length > 0);
            
            if (!hasInitiatives) {
              return (
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-3">
                      <Lightbulb className="h-6 w-6 text-yellow-600" />
                      <div>
                        <h3 className="font-medium text-yellow-800">Create Initiatives First</h3>
                        <p className="text-yellow-700 text-sm">
                          You need to create strategic initiatives before adding revenue streams.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            }

            const allStreams = [];
            framework?.objectives?.forEach(obj => {
              obj.initiatives?.forEach(init => {
                init.revenue_streams?.forEach(stream => {
                  allStreams.push({...stream, initiative: init, objective: obj});
                });
              });
            });
            
            return allStreams.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allStreams.map((stream) => (
                  <Card key={stream.id} className="glass-card shadow-soft">
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <DollarSign className="h-5 w-5 mr-2 text-purple-600" />
                            <CardTitle className="text-lg mr-2">{stream.stream_name}</CardTitle>
                            <Badge variant="outline" className="text-xs">
                              {stream.stream_type}
                            </Badge>
                          </div>
                          <p className="text-sm text-purple-600 font-medium mb-1">{stream.stream_id}</p>
                          <p className="text-xs text-green-600 mb-1">→ {stream.initiative.initiative_name}</p>
                          <p className="text-xs text-blue-600 mb-2">→ {stream.objective.objective_name}</p>
                          <CardDescription>{stream.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-xs">
                          {stream.products?.length || 0} Product{(stream.products?.length || 0) !== 1 ? 's' : ''}
                        </Badge>
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditStream(stream)}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteStream(stream.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="glass-card shadow-soft">
                <CardContent className="p-12 text-center">
                  <DollarSign className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No Revenue Streams Yet</h3>
                  <p className="text-slate-600 mb-6">
                    Create revenue streams to define how your initiatives will generate income.
                  </p>
                  <Button onClick={() => {setShowStreamDialog(true); resetStreamForm();}} className="btn-primary">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Revenue Stream
                  </Button>
                </CardContent>
              </Card>
            );
          })()}
        </TabsContent>

        {/* Products & Services Tab */}
        <TabsContent value="products" className="space-y-6">
          {/* Products Header */}
          <div className="flex items-center justify-between">
            <div>
              <TooltipLabel 
                htmlFor="products-header" 
                tooltip={strategicFrameworkTooltips.sections.products}
                className="text-2xl font-bold text-slate-900 mb-2"
              >
                Products & Services
              </TooltipLabel>
              <p className="text-slate-600">Manage products and services linked to your strategic framework</p>
            </div>
            <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
              <DialogTrigger asChild>
                <Button 
                  onClick={resetProductForm} 
                  className="btn-primary"
                  disabled={getAllRevenueStreams().length === 0}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product/Service
                </Button>
              </DialogTrigger>
            </Dialog>
          </div>

          {getAllRevenueStreams().length === 0 && (
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <Target className="h-6 w-6 text-yellow-600" />
                  <div>
                    <h3 className="font-medium text-yellow-800">Create Strategic Framework First</h3>
                    <p className="text-yellow-700 text-sm">
                      You need to create objectives, initiatives, and revenue streams before adding products.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Products Grid */}
          {products.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <Card key={product.id} className="glass-card shadow-soft">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <CardTitle className="text-lg mr-2">{product.name}</CardTitle>
                          <Badge variant={product.active ? "default" : "secondary"} className="text-xs mr-2">
                            {product.active ? (
                              <>
                                <Eye className="h-3 w-3 mr-1" />
                                Active
                              </>
                            ) : (
                              <>
                                <EyeOff className="h-3 w-3 mr-1" />
                                Inactive
                              </>
                            )}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {product.type === 'services' ? 'Service' : 'Product'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500 font-mono">{product.sku}</p>
                        {product.category && (
                          <p className="text-xs text-blue-600 font-medium mt-1">{product.category}</p>
                        )}
                        <CardDescription className="mt-2">
                          {product.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Pricing */}
                    <div className="bg-green-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-green-700 font-medium">Pricing</span>
                        <span className="text-lg font-bold text-green-800">
                          {formatCurrency(product.base_price, product.currency || currentPlan?.currency)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-green-600">
                        <span>COGS: {product.cogs_percentage}%</span>
                        <span>Margin: {(100 - product.cogs_percentage).toFixed(1)}%</span>
                      </div>
                    </div>

                    {/* Growth Metrics */}
                    {product.annual_sales_volume_growth && (
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-blue-700 font-medium">Annual Growth</span>
                          <span className="text-sm font-bold text-blue-800">
                            {product.annual_sales_volume_growth}%
                          </span>
                        </div>
                        {product.base_year_units && (
                          <p className="text-xs text-blue-600">
                            Base: {product.base_year_units.toLocaleString()} units
                          </p>
                        )}
                      </div>
                    )}

                    {/* Strategic Link */}
                    {product.revenue_stream_info && (
                      <div className="bg-purple-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-purple-700 font-medium">Revenue Stream</span>
                        </div>
                        <p className="text-xs text-purple-600 font-medium">
                          {product.revenue_stream_info.stream_name}
                        </p>
                        {product.initiative_info && (
                          <p className="text-xs text-purple-500">
                            → {product.initiative_info.initiative_name}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditProduct(product)}
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteProduct(product.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="text-xs text-slate-500">
                        {product.launch_year && `Launch: ${product.launch_year}`}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : getAllRevenueStreams().length > 0 ? (
            <Card className="glass-card shadow-soft">
              <CardContent className="p-12 text-center">
                <Package className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Products or Services Yet</h3>
                <p className="text-slate-600 mb-6">
                  Start by adding your first product or service to your revenue streams.
                </p>
                <Button onClick={() => {setShowProductDialog(true); resetProductForm();}} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Product/Service
                </Button>
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>



        {/* Sales Tab */}
        <TabsContent value="sales" className="space-y-6">
          {/* Sales Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Sales Analytics & Forecasting</h2>
              <p className="text-slate-600">Comprehensive sales analytics with charts, projections, and performance insights</p>
            </div>
            <Button
              onClick={() => fetchProducts()}
              variant="outline"
              size="sm"
              className="flex items-center space-x-2"
            >
              <TrendingUp className="h-4 w-4" />
              <span>Refresh Data</span>
            </Button>
          </div>

          {products.length === 0 && (
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <Package className="h-6 w-6 text-yellow-600" />
                  <div>
                    <h3 className="font-medium text-yellow-800">Create Products First</h3>
                    <p className="text-yellow-700 text-sm">
                      You need to create products and services with growth rates before accessing sales analytics.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => setActiveTab("products")}
                    >
                      Go to Products Tab
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sales Analytics Dashboard */}
          {products.length > 0 && (
            <div className="space-y-8">
              {/* Sales Overview Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {(() => {
                  const totalProducts = products.length;
                  const productsWithGrowth = products.filter(p => (p.annual_sales_volume_growth || p.growth_rates_per_year) && p.base_year_units);
                  const totalRevenue2025 = productsWithGrowth.reduce((sum, p) => 
                    sum + (p.base_year_units * p.base_price), 0
                  );
                  const totalRevenue2029 = productsWithGrowth.reduce((sum, p) => {
                    const projectedUnits = calculateProjectedUnits(p, 2029);
                    return sum + (projectedUnits * p.base_price);
                  }, 0);
                  const avgGrowthRate = productsWithGrowth.length > 0 
                    ? productsWithGrowth.reduce((sum, p) => sum + p.annual_sales_volume_growth, 0) / productsWithGrowth.length 
                    : 0;

                  return (
                    <>
                      <Card className="glass-card">
                        <CardContent className="p-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Package className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm text-slate-600">Total Products</p>
                              <p className="text-2xl font-bold text-slate-900">{totalProducts}</p>
                              <p className="text-xs text-blue-600">
                                {products.filter(p => p.type === 'products').length}P / {products.filter(p => p.type === 'services').length}S
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="glass-card">
                        <CardContent className="p-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <DollarSign className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm text-slate-600">2025 Revenue</p>
                              <p className="text-2xl font-bold text-green-700">
                                {formatCurrency(totalRevenue2025, currentPlan?.currency)}
                              </p>
                              <p className="text-xs text-green-600">Base year projection</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="glass-card">
                        <CardContent className="p-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <TrendingUp className="h-5 w-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-sm text-slate-600">2029 Revenue</p>
                              <p className="text-2xl font-bold text-purple-700">
                                {formatCurrency(totalRevenue2029, currentPlan?.currency)}
                              </p>
                              <p className="text-xs text-purple-600">5-year projection</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="glass-card">
                        <CardContent className="p-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-orange-100 rounded-lg">
                              <BarChart3 className="h-5 w-5 text-orange-600" />
                            </div>
                            <div>
                              <p className="text-sm text-slate-600">Avg Growth</p>
                              <p className="text-2xl font-bold text-orange-700">{avgGrowthRate.toFixed(1)}%</p>
                              <p className="text-xs text-orange-600">Annual growth rate</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  );
                })()}
              </div>

              {/* Comprehensive Sales Forecasting Table */}
              <Card className="glass-card shadow-soft">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center">
                    <Package className="h-5 w-5 mr-2 text-blue-600" />
                    Sales Forecasting Matrix
                  </CardTitle>
                  <CardDescription>
                    Comprehensive view of all products/services with units, revenue, and growth projections
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b-2 border-slate-300 bg-slate-100">
                          <th className="text-left py-3 px-4 font-semibold text-slate-800 min-w-[180px]">Product/Service</th>
                          <th className="text-center py-3 px-4 font-semibold text-slate-800">Type</th>
                          <th className="text-center py-3 px-4 font-semibold text-slate-800">Price</th>
                          <th className="text-center py-3 px-4 font-semibold text-slate-800">Growth %</th>
                          <th className="text-center py-3 px-4 font-semibold text-slate-800">Base Units</th>
                          {[2025, 2026, 2027, 2028, 2029].map(year => (
                            <th key={year} className="text-center py-3 px-4 font-semibold text-slate-800 border-l border-slate-200">
                              <div className="flex flex-col">
                                <span>{year}</span>
                                <span className="text-xs font-normal text-slate-600">Units / Revenue</span>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((product, index) => (
                          <tr key={product.id} className={`border-b border-slate-200 hover:bg-slate-50 ${index % 2 === 0 ? 'bg-slate-25' : ''}`}>
                            <td className="py-4 px-4">
                              <div className="flex items-center space-x-3">
                                <div className={`w-3 h-3 rounded-full ${product.type === 'services' ? 'bg-purple-500' : 'bg-blue-500'}`}></div>
                                <div>
                                  <p className="font-medium text-slate-900">{product.name}</p>
                                  <p className="text-sm text-slate-500">{product.sku}</p>
                                  <p className="text-xs text-slate-500">{product.category || 'Uncategorized'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-4 text-center">
                              <Badge variant={product.type === 'services' ? 'secondary' : 'default'} className="text-xs">
                                {product.type === 'services' ? 'Service' : 'Product'}
                              </Badge>
                            </td>
                            <td className="py-4 px-4 text-center">
                              <div className="font-medium text-slate-900">
                                {formatCurrency(product.base_price, product.currency || currentPlan?.currency)}
                              </div>
                            </td>
                            <td className="py-4 px-4 text-center">
                              {product.growth_rates_per_year && Object.keys(product.growth_rates_per_year).length > 0 ? (
                                <div className="flex items-center justify-center">
                                  <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                                  <span className="font-medium text-green-700 text-xs">Per Year</span>
                                </div>
                              ) : product.annual_sales_volume_growth ? (
                                <div className="flex items-center justify-center">
                                  <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                                  <span className="font-medium text-green-700">{product.annual_sales_volume_growth}%</span>
                                </div>
                              ) : (
                                <Badge variant="outline" className="text-slate-500">No Growth</Badge>
                              )}
                            </td>
                            <td className="py-4 px-4 text-center">
                              <div className="font-medium text-slate-900">
                                {product.base_year_units ? product.base_year_units.toLocaleString() : '-'}
                              </div>
                            </td>
                            {[2025, 2026, 2027, 2028, 2029].map((year, yearIndex) => {
                              let projectedUnits = 0;
                              let projectedRevenue = 0;
                              
                              if (product.base_year_units) {
                                projectedUnits = Math.round(calculateProjectedUnits(product, year));
                                projectedRevenue = projectedUnits * product.base_price;
                              }

                              return (
                                <td key={year} className="py-4 px-4 text-center border-l border-slate-200">
                                  {projectedUnits > 0 ? (
                                    <div className="space-y-1">
                                      <div className="font-bold text-slate-900">
                                        {projectedUnits.toLocaleString()}
                                      </div>
                                      <div className="text-sm font-medium text-green-600">
                                        {formatCurrency(projectedRevenue, currentPlan?.currency)}
                                      </div>
                                      {yearIndex > 0 && (
                                        <div className="text-xs text-blue-600">
                                          +{((projectedUnits / (product.base_year_units * Math.pow(1 + (product.annual_sales_volume_growth / 100), yearIndex - 1)) - 1) * 100).toFixed(0)}%
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-slate-400 text-sm">No Data</div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                        
                        {/* Totals Row */}
                        <tr className="border-t-2 border-slate-400 bg-gradient-to-r from-slate-100 to-slate-200 font-semibold">
                          <td className="py-4 px-4 font-bold text-slate-900">Portfolio Totals</td>
                          <td className="py-4 px-4 text-center text-slate-700">
                            {products.filter(p => p.type === 'products').length}P/{products.filter(p => p.type === 'services').length}S
                          </td>
                          <td className="py-4 px-4 text-center text-slate-700">Avg Price</td>
                          <td className="py-4 px-4 text-center text-slate-700">
                            {products.filter(p => p.annual_sales_volume_growth).length > 0
                              ? (products.filter(p => p.annual_sales_volume_growth).reduce((sum, p) => sum + p.annual_sales_volume_growth, 0) / products.filter(p => p.annual_sales_volume_growth).length).toFixed(1) + '%'
                              : 'N/A'
                            }
                          </td>
                          <td className="py-4 px-4 text-center text-slate-700">
                            {products.filter(p => p.base_year_units).reduce((sum, p) => sum + p.base_year_units, 0).toLocaleString()}
                          </td>
                          {[2025, 2026, 2027, 2028, 2029].map((year, yearIndex) => {
                            const totalUnits = products
                              .filter(p => p.annual_sales_volume_growth && p.base_year_units)
                              .reduce((sum, p) => {
                                const units = Math.round(
                                  p.base_year_units * Math.pow(1 + (p.annual_sales_volume_growth / 100), yearIndex)
                                );
                                return sum + units;
                              }, 0);
                            
                            const totalRevenue = products
                              .filter(p => p.annual_sales_volume_growth && p.base_year_units)
                              .reduce((sum, p) => {
                                const units = Math.round(
                                  p.base_year_units * Math.pow(1 + (p.annual_sales_volume_growth / 100), yearIndex)
                                );
                                return sum + (units * p.base_price);
                              }, 0);

                            return (
                              <td key={year} className="py-4 px-4 text-center border-l border-slate-300">
                                <div className="space-y-1">
                                  <div className="font-bold text-slate-900">
                                    {totalUnits.toLocaleString()}
                                  </div>
                                  <div className="text-sm font-bold text-green-700">
                                    {formatCurrency(totalRevenue, currentPlan?.currency)}
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Charts and Graphs Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Revenue Trend Chart */}
                <Card className="glass-card shadow-soft">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
                      Portfolio Revenue Trend
                    </CardTitle>
                    <CardDescription>5-year revenue projection across all products and services</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={(() => {
                          return [2025, 2026, 2027, 2028, 2029].map((year, yearIndex) => {
                            const totalRevenue = products
                              .filter(p => p.annual_sales_volume_growth && p.base_year_units)
                              .reduce((sum, p) => {
                                const units = Math.round(
                                  p.base_year_units * Math.pow(1 + (p.annual_sales_volume_growth / 100), yearIndex)
                                );
                                return sum + (units * p.base_price);
                              }, 0);
                            
                            return {
                              year: year.toString(),
                              revenue: totalRevenue / 1000000, // Convert to millions
                              units: products
                                .filter(p => p.annual_sales_volume_growth && p.base_year_units)
                                .reduce((sum, p) => {
                                  const units = Math.round(
                                    p.base_year_units * Math.pow(1 + (p.annual_sales_volume_growth / 100), yearIndex)
                                  );
                                  return sum + units;
                                }, 0)
                            };
                          });
                        })()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="year" stroke="#64748b" />
                          <YAxis stroke="#64748b" />
                          <RechartsTooltip 
                            formatter={(value, name) => {
                              if (name === 'revenue') {
                                return [formatCurrencyContext(value * 1000000), 'Revenue'];
                              }
                              return [`${value.toLocaleString()}`, 'Units'];
                            }}
                            labelStyle={{ color: '#475569' }}
                            contentStyle={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="revenue" 
                            stroke="#3b82f6" 
                            strokeWidth={3}
                            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }}
                            activeDot={{ r: 8, fill: '#1d4ed8' }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Product Revenue Breakdown */}
                <Card className="glass-card shadow-soft">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <BarChart3 className="h-5 w-5 mr-2 text-green-600" />
                      Revenue by Product/Service
                    </CardTitle>
                    <CardDescription>2029 projected revenue breakdown by product line</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={(() => {
                          return products
                            .filter(p => p.annual_sales_volume_growth && p.base_year_units)
                            .map(product => {
                              const projectedUnits = Math.round(
                                product.base_year_units * Math.pow(1 + (product.annual_sales_volume_growth / 100), 4)
                              );
                              const projectedRevenue = projectedUnits * product.base_price;
                              
                              return {
                                name: product.name.substring(0, 15) + (product.name.length > 15 ? '...' : ''),
                                revenue: projectedRevenue / 1000, // Convert to thousands
                                type: product.type,
                                sku: product.sku
                              };
                            })
                            .sort((a, b) => b.revenue - a.revenue)
                            .slice(0, 8); // Show top 8 products
                        })()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis 
                            dataKey="name" 
                            stroke="#64748b" 
                            angle={-45}
                            textAnchor="end"
                            height={100}
                            fontSize={12}
                          />
                          <YAxis stroke="#64748b" />
                          <RechartsTooltip 
                            formatter={(value, name, props) => [
                              formatCurrencyContext(value * 1000), 
                              'Projected Revenue (2029)'
                            ]}
                            labelFormatter={(label) => `Product: ${label}`}
                            contentStyle={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px'
                            }}
                          />
                          <Bar 
                            dataKey="revenue" 
                            fill="#10b981"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Units Growth Chart */}
                <Card className="glass-card shadow-soft">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2 text-purple-600" />
                      Units Sold Projection
                    </CardTitle>
                    <CardDescription>Total units sold across all products over 5 years</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={(() => {
                          return [2025, 2026, 2027, 2028, 2029].map((year, yearIndex) => {
                            const totalUnits = products
                              .filter(p => p.annual_sales_volume_growth && p.base_year_units)
                              .reduce((sum, p) => {
                                const units = Math.round(
                                  p.base_year_units * Math.pow(1 + (p.annual_sales_volume_growth / 100), yearIndex)
                                );
                                return sum + units;
                              }, 0);
                            
                            return {
                              year: year.toString(),
                              totalUnits: totalUnits,
                              productsUnits: products
                                .filter(p => p.type === 'products' && p.annual_sales_volume_growth && p.base_year_units)
                                .reduce((sum, p) => {
                                  const units = Math.round(
                                    p.base_year_units * Math.pow(1 + (p.annual_sales_volume_growth / 100), yearIndex)
                                  );
                                  return sum + units;
                                }, 0),
                              servicesUnits: products
                                .filter(p => p.type === 'services' && p.annual_sales_volume_growth && p.base_year_units)
                                .reduce((sum, p) => {
                                  const units = Math.round(
                                    p.base_year_units * Math.pow(1 + (p.annual_sales_volume_growth / 100), yearIndex)
                                  );
                                  return sum + units;
                                }, 0)
                            };
                          });
                        })()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="year" stroke="#64748b" />
                          <YAxis stroke="#64748b" />
                          <RechartsTooltip 
                            formatter={(value, name) => {
                              return [`${value.toLocaleString()}`, name === 'productsUnits' ? 'Products' : name === 'servicesUnits' ? 'Services' : 'Total Units'];
                            }}
                            contentStyle={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px'
                            }}
                          />
                          <Area type="monotone" dataKey="productsUnits" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                          <Area type="monotone" dataKey="servicesUnits" stackId="1" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.6} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Growth Rate Distribution */}
                <Card className="glass-card shadow-soft">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <Zap className="h-5 w-5 mr-2 text-yellow-600" />
                      Growth Rate Distribution
                    </CardTitle>
                    <CardDescription>Distribution of growth rates across your product portfolio</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={(() => {
                          const growthRanges = [
                            { range: '0-5%', min: 0, max: 5, color: '#ef4444' },
                            { range: '5-10%', min: 5, max: 10, color: '#f97316' },
                            { range: '10-15%', min: 10, max: 15, color: '#eab308' },
                            { range: '15-20%', min: 15, max: 20, color: '#22c55e' },
                            { range: '20%+', min: 20, max: 100, color: '#10b981' }
                          ];
                          
                          return growthRanges.map(range => {
                            const count = products.filter(p => 
                              p.annual_sales_volume_growth >= range.min && 
                              p.annual_sales_volume_growth < (range.max === 100 ? 1000 : range.max)
                            ).length;
                            
                            return {
                              range: range.range,
                              count: count,
                              fill: range.color
                            };
                          });
                        })()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="range" stroke="#64748b" />
                          <YAxis stroke="#64748b" />
                          <RechartsTooltip 
                            formatter={(value) => [`${value}`, 'Products']}
                            contentStyle={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px'
                            }}
                          />
                          <Bar dataKey="count" fill="#8884d8" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Key Insights Summary */}
              <Card className="glass-card shadow-soft bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center text-blue-900">
                    <Lightbulb className="h-6 w-6 mr-2 text-yellow-500" />
                    Sales Analytics Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {(() => {
                      const productsWithGrowth = products.filter(p => p.annual_sales_volume_growth && p.base_year_units);
                      const revenue2025 = productsWithGrowth.reduce((sum, p) => sum + (p.base_year_units * p.base_price), 0);
                      const revenue2029 = productsWithGrowth.reduce((sum, p) => {
                        const units = p.base_year_units * Math.pow(1 + (p.annual_sales_volume_growth / 100), 4);
                        return sum + (units * p.base_price);
                      }, 0);
                      const cagr = revenue2025 > 0 ? (Math.pow(revenue2029 / revenue2025, 1/5) - 1) * 100 : 0;
                      const topGrowthProduct = products
                        .filter(p => p.annual_sales_volume_growth)
                        .sort((a, b) => b.annual_sales_volume_growth - a.annual_sales_volume_growth)[0];
                      const avgPrice = products.length > 0 ? products.reduce((sum, p) => sum + p.base_price, 0) / products.length : 0;

                      return (
                        <>
                          <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-blue-900 mb-2">Portfolio CAGR</h4>
                            <p className="text-3xl font-bold text-blue-700 mb-1">{cagr.toFixed(1)}%</p>
                            <p className="text-sm text-blue-600">5-year compound annual growth rate</p>
                          </div>

                          <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-blue-900 mb-2">Top Growth Product</h4>
                            <p className="text-lg font-bold text-blue-700 mb-1">
                              {topGrowthProduct ? topGrowthProduct.name : 'N/A'}
                            </p>
                            <p className="text-sm text-blue-600">
                              {topGrowthProduct ? `${topGrowthProduct.annual_sales_volume_growth}% growth` : 'No growth data'}
                            </p>
                          </div>

                          <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-blue-900 mb-2">Average Price Point</h4>
                            <p className="text-2xl font-bold text-blue-700 mb-1">
                              {formatCurrency(avgPrice, currentPlan?.currency)}
                            </p>
                            <p className="text-sm text-blue-600">Across all products/services</p>
                          </div>

                          <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-blue-900 mb-2">Revenue Growth</h4>
                            <p className="text-2xl font-bold text-green-700 mb-1">
                              {formatCurrency(revenue2029 - revenue2025, currentPlan?.currency)}
                            </p>
                            <p className="text-sm text-blue-600">Expected increase (2025-2029)</p>
                          </div>

                          <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-blue-900 mb-2">Portfolio Mix</h4>
                            <p className="text-lg font-bold text-blue-700 mb-1">
                              {((products.filter(p => p.type === 'products').length / products.length) * 100).toFixed(0)}% Products
                            </p>
                            <p className="text-sm text-blue-600">
                              {((products.filter(p => p.type === 'services').length / products.length) * 100).toFixed(0)}% Services
                            </p>
                          </div>

                          <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-blue-900 mb-2">Data Coverage</h4>
                            <p className="text-lg font-bold text-blue-700 mb-1">
                              {Math.round((productsWithGrowth.length / products.length) * 100)}%
                            </p>
                            <p className="text-sm text-blue-600">
                              {productsWithGrowth.length}/{products.length} have growth data
                            </p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs - Add Objective Dialog */}
      <Dialog open={showObjectiveDialog} onOpenChange={setShowObjectiveDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Edit Strategic Objective" : "Add New Strategic Objective"}
            </DialogTitle>
            <DialogDescription>
              Create a high-level strategic objective that will guide your business initiatives and revenue streams.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateObjective} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <TooltipLabel htmlFor="objective-id" tooltip={strategicFrameworkTooltips.objectives.objectiveId}>
                  Objective ID *
                </TooltipLabel>
                <Input
                  id="objective-id"
                  placeholder="e.g., OBJ-001"
                  value={objectiveForm.objective_id}
                  onChange={(e) => setObjectiveForm(prev => ({...prev, objective_id: e.target.value}))}
                  className="form-input"
                />
              </div>

              <div className="space-y-2">
                <TooltipLabel htmlFor="pillar" tooltip={strategicFrameworkTooltips.objectives.strategicPillar}>
                  Strategic Pillar *
                </TooltipLabel>
                <Select 
                  value={objectiveForm.pillar} 
                  onValueChange={(value) => setObjectiveForm(prev => ({...prev, pillar: value}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select strategic pillar" />
                  </SelectTrigger>
                  <SelectContent>
                    {standardPillars.map(pillar => (
                      <SelectItem key={pillar} value={pillar}>
                        {pillar}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <TooltipLabel htmlFor="objective-name" tooltip={strategicFrameworkTooltips.objectives.objectiveName}>
                Objective Name *
              </TooltipLabel>
              <Input
                id="objective-name"
                placeholder="e.g., Expand Specialized Healthcare Services"
                value={objectiveForm.objective_name}
                onChange={(e) => setObjectiveForm(prev => ({...prev, objective_name: e.target.value}))}
                className="form-input"
              />
            </div>

            <div className="space-y-2">
              <TooltipLabel htmlFor="description" tooltip={strategicFrameworkTooltips.objectives.description}>
                Description
              </TooltipLabel>
              <Textarea
                id="description"
                placeholder="Detailed description of the strategic objective..."
                value={objectiveForm.description}
                onChange={(e) => setObjectiveForm(prev => ({...prev, description: e.target.value}))}
                className="form-input"
                rows={3}
              />
            </div>

            {!editingItem && (
              <div className="border rounded-lg p-4 bg-blue-50">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="create-initiative"
                    checked={objectiveForm.createInitiative}
                    onChange={(e) => setObjectiveForm(prev => ({...prev, createInitiative: e.target.checked}))}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <Label htmlFor="create-initiative" className="text-blue-800 font-medium">
                      Also create initiatives
                    </Label>
                    <p className="text-xs text-blue-600 mt-1">
                      Quickly add multiple initiatives under this objective
                    </p>
                    {objectiveForm.createInitiative && (
                      <div className="mt-3 space-y-3">
                        {objectiveForm.initiatives.map((initiative, index) => (
                          <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-white rounded border">
                            <Input
                              placeholder={`Initiative ${index + 1} name`}
                              value={initiative.name}
                              onChange={(e) => {
                                const newInitiatives = [...objectiveForm.initiatives];
                                newInitiatives[index].name = e.target.value;
                                setObjectiveForm(prev => ({...prev, initiatives: newInitiatives}));
                              }}
                              className="form-input"
                            />
                            <div className="flex space-x-2">
                              <Input
                                placeholder="Description (optional)"
                                value={initiative.description}
                                onChange={(e) => {
                                  const newInitiatives = [...objectiveForm.initiatives];
                                  newInitiatives[index].description = e.target.value;
                                  setObjectiveForm(prev => ({...prev, initiatives: newInitiatives}));
                                }}
                                className="form-input flex-1"
                              />
                              {objectiveForm.initiatives.length > 1 && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const newInitiatives = objectiveForm.initiatives.filter((_, i) => i !== index);
                                    setObjectiveForm(prev => ({...prev, initiatives: newInitiatives}));
                                  }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setObjectiveForm(prev => ({
                              ...prev, 
                              initiatives: [...prev.initiatives, { name: "", description: "" }]
                            }));
                          }}
                          className="text-blue-600"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Another Initiative
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <TooltipLabel htmlFor="owner" tooltip={strategicFrameworkTooltips.objectives.owner}>
                  Owner
                </TooltipLabel>
                <Select 
                  value={objectiveForm.ownerCustom ? "__custom" : (objectiveForm.owner || "")} 
                  onValueChange={(val) => {
                    if (val === "__custom") {
                      setObjectiveForm(prev => ({...prev, owner: "", ownerCustom: true}));
                    } else {
                      setObjectiveForm(prev => ({...prev, owner: val, ownerCustom: false}));
                    }
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select team member" /></SelectTrigger>
                  <SelectContent>
                    {teamMembers.map(member => (
                      <SelectItem key={member.id} value={member.name}>
                        {member.name} {member.role && `(${member.role})`}
                      </SelectItem>
                    ))}
                    <SelectItem value="__custom">+ Enter Custom Name</SelectItem>
                  </SelectContent>
                </Select>
                {objectiveForm.ownerCustom && (
                  <Input 
                    autoFocus
                    placeholder="Enter custom owner name"
                    value={objectiveForm.owner}
                    onChange={(e) => setObjectiveForm(prev => ({...prev, owner: e.target.value}))}
                    className="mt-2"
                  />
                )}
              </div>

              <div className="space-y-2">
                <TooltipLabel htmlFor="start-date" tooltip={strategicFrameworkTooltips.objectives.targetDate}>
                  Start Date
                </TooltipLabel>
                <Input
                  id="start-date"
                  type="date"
                  value={objectiveForm.start_date}
                  onChange={(e) => setObjectiveForm(prev => ({...prev, start_date: e.target.value}))}
                  className="form-input"
                />
              </div>

              <div className="space-y-2">
                <TooltipLabel htmlFor="end-date" tooltip={strategicFrameworkTooltips.objectives.targetDate}>
                  End Date
                </TooltipLabel>
                <Input
                  id="end-date"
                  type="date"
                  value={objectiveForm.end_date}
                  onChange={(e) => setObjectiveForm(prev => ({...prev, end_date: e.target.value}))}
                  className="form-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <TooltipLabel htmlFor="status" tooltip={strategicFrameworkTooltips.objectives.status}>
                Status
              </TooltipLabel>
              <Select 
                value={objectiveForm.status} 
                onValueChange={(value) => setObjectiveForm(prev => ({...prev, status: value}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {standardStatuses.map(status => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowObjectiveDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                {editingItem ? "Update Objective" : "Create Objective"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Initiative Dialog */}
      <Dialog open={showInitiativeDialog} onOpenChange={setShowInitiativeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit Strategic Initiative' : 'Add New Strategic Initiative'}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? `Edit initiative "${editingItem.initiative_name}"` : selectedObjective ? `Create an initiative under "${selectedObjective?.objective_name}" objective.` : "Create a strategic initiative."}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateInitiative} className="space-y-6">
            <div className="space-y-2">
              <TooltipLabel htmlFor="linked-objective" tooltip={strategicFrameworkTooltips.initiatives.linkedObjective}>
                Linked Strategic Objective *
              </TooltipLabel>
              <Select 
                value={initiativeForm.linked_objective_id} 
                onValueChange={(value) => setInitiativeForm(prev => ({...prev, linked_objective_id: value}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select objective to link this initiative to" />
                </SelectTrigger>
                <SelectContent>
                  {framework?.objectives?.map(objective => (
                    <SelectItem key={objective.id} value={objective.id}>
                      {objective.objective_id} - {objective.objective_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <TooltipLabel htmlFor="initiative-id" tooltip={strategicFrameworkTooltips.initiatives.initiativeId}>
                  Initiative ID *
                </TooltipLabel>
                <Input
                  id="initiative-id"
                  placeholder="e.g., INI-001"
                  value={initiativeForm.initiative_id}
                  onChange={(e) => setInitiativeForm(prev => ({...prev, initiative_id: e.target.value}))}
                  className="form-input"
                />
              </div>

              <div className="space-y-2">
                <TooltipLabel htmlFor="budget" tooltip={strategicFrameworkTooltips.initiatives.budget}>
                  Budget ($)
                </TooltipLabel>
                <Input
                  id="budget"
                  type="number"
                  placeholder="e.g., 500000"
                  value={initiativeForm.budget}
                  onChange={(e) => setInitiativeForm(prev => ({...prev, budget: e.target.value}))}
                  className="form-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <TooltipLabel htmlFor="initiative-name" tooltip={strategicFrameworkTooltips.initiatives.initiativeName}>
                Initiative Name *
              </TooltipLabel>
              <Input
                id="initiative-name"
                placeholder="e.g., Launch Medical Plaza"
                value={initiativeForm.initiative_name}
                onChange={(e) => setInitiativeForm(prev => ({...prev, initiative_name: e.target.value}))}
                className="form-input"
              />
            </div>

            <div className="space-y-2">
              <TooltipLabel htmlFor="initiative-description" tooltip={strategicFrameworkTooltips.initiatives.description}>
                Description
              </TooltipLabel>
              <Textarea
                id="initiative-description"
                placeholder="Detailed description of the initiative..."
                value={initiativeForm.description}
                onChange={(e) => setInitiativeForm(prev => ({...prev, description: e.target.value}))}
                className="form-input"
                rows={3}
              />
            </div>

            <div className="border rounded-lg p-4 bg-green-50">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="create-revenue-stream"
                  checked={initiativeForm.createRevenueStream}
                  onChange={(e) => setInitiativeForm(prev => ({...prev, createRevenueStream: e.target.checked}))}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor="create-revenue-stream" className="text-green-800 font-medium">
                    Also create revenue streams
                  </Label>
                  <p className="text-xs text-green-600 mt-1">
                    Quickly add multiple revenue streams under this initiative
                  </p>
                  {initiativeForm.createRevenueStream && (
                    <div className="mt-3 space-y-3">
                      {initiativeForm.revenueStreams.map((stream, index) => (
                        <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-white rounded border">
                          <Input
                            placeholder={`Revenue Stream ${index + 1} name`}
                            value={stream.name}
                            onChange={(e) => {
                              const newStreams = [...initiativeForm.revenueStreams];
                              newStreams[index].name = e.target.value;
                              setInitiativeForm(prev => ({...prev, revenueStreams: newStreams}));
                            }}
                            className="form-input"
                          />
                          <div className="flex space-x-2">
                            <Select 
                              value={stream.type} 
                              onValueChange={(value) => {
                                const newStreams = [...initiativeForm.revenueStreams];
                                newStreams[index].type = value;
                                setInitiativeForm(prev => ({...prev, revenueStreams: newStreams}));
                              }}
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {standardStreamTypes.map(type => (
                                  <SelectItem key={type} value={type}>
                                    {type}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {initiativeForm.revenueStreams.length > 1 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const newStreams = initiativeForm.revenueStreams.filter((_, i) => i !== index);
                                  setInitiativeForm(prev => ({...prev, revenueStreams: newStreams}));
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setInitiativeForm(prev => ({
                            ...prev, 
                            revenueStreams: [...prev.revenueStreams, { name: "", type: "Hybrid Revenue" }]
                          }));
                        }}
                        className="text-green-600"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Another Revenue Stream
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <TooltipLabel htmlFor="timeline-start" tooltip={strategicFrameworkTooltips.initiatives.timelineStart}>
                  Timeline Start
                </TooltipLabel>
                <Input
                  id="timeline-start"
                  type="date"
                  value={initiativeForm.timeline_start}
                  onChange={(e) => setInitiativeForm(prev => ({...prev, timeline_start: e.target.value}))}
                  className="form-input"
                />
              </div>

              <div className="space-y-2">
                <TooltipLabel htmlFor="timeline-end" tooltip={strategicFrameworkTooltips.initiatives.timelineEnd}>
                  Timeline End
                </TooltipLabel>
                <Input
                  id="timeline-end"
                  type="date"
                  value={initiativeForm.timeline_end}
                  onChange={(e) => setInitiativeForm(prev => ({...prev, timeline_end: e.target.value}))}
                  className="form-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <TooltipLabel htmlFor="init-owner" tooltip={strategicFrameworkTooltips.initiatives.owner}>
                  Owner
                </TooltipLabel>
                <Select 
                  value={initiativeForm.ownerCustom ? "__custom" : (initiativeForm.owner || "")} 
                  onValueChange={(val) => {
                    if (val === "__custom") {
                      setInitiativeForm(prev => ({...prev, owner: "", ownerCustom: true}));
                    } else {
                      setInitiativeForm(prev => ({...prev, owner: val, ownerCustom: false}));
                    }
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select team member" /></SelectTrigger>
                  <SelectContent>
                    {teamMembers.map(member => (
                      <SelectItem key={member.id} value={member.name}>
                        {member.name} {member.role && `(${member.role})`}
                      </SelectItem>
                    ))}
                    <SelectItem value="__custom">+ Enter Custom Name</SelectItem>
                  </SelectContent>
                </Select>
                {initiativeForm.ownerCustom && (
                  <Input 
                    autoFocus
                    placeholder="Enter custom owner name"
                    value={initiativeForm.owner}
                    onChange={(e) => setInitiativeForm(prev => ({...prev, owner: e.target.value}))}
                    className="mt-2"
                  />
                )}
              </div>

              <div className="space-y-2">
                <TooltipLabel htmlFor="init-status" tooltip={strategicFrameworkTooltips.initiatives.status}>
                  Status
                </TooltipLabel>
                <Select 
                  value={initiativeForm.status} 
                  onValueChange={(value) => setInitiativeForm(prev => ({...prev, status: value}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {standardStatuses.map(status => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowInitiativeDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                {editingItem ? 'Update Initiative' : 'Create Initiative'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Revenue Stream Dialog */}
      <Dialog open={showStreamDialog} onOpenChange={setShowStreamDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit Revenue Stream" : "Add New Revenue Stream"}</DialogTitle>
            <DialogDescription>
              {editingItem ? `Edit revenue stream "${editingItem.stream_name}"` : selectedInitiative ? `Create a revenue stream under "${selectedInitiative?.initiative_name}" initiative.` : "Create a revenue stream."}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateStream} className="space-y-6">
            <div className="space-y-2">
              <TooltipLabel htmlFor="linked-objective-stream" tooltip={strategicFrameworkTooltips.revenueStreams.linkedObjective}>
                Strategic Objective *
              </TooltipLabel>
              <Select 
                value={streamForm.linked_objective_id} 
                onValueChange={(value) => {
                  setStreamForm(prev => ({
                    ...prev, 
                    linked_objective_id: value,
                    linked_initiative_id: "" // Reset initiative when objective changes
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select objective first" />
                </SelectTrigger>
                <SelectContent>
                  {framework?.objectives?.map(objective => (
                    <SelectItem key={objective.id} value={objective.id}>
                      {objective.objective_id} - {objective.objective_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <TooltipLabel htmlFor="linked-initiative-stream" tooltip={strategicFrameworkTooltips.revenueStreams.linkedInitiative}>
                Strategic Initiative *
              </TooltipLabel>
              <Select 
                value={streamForm.linked_initiative_id} 
                onValueChange={(value) => setStreamForm(prev => ({...prev, linked_initiative_id: value}))}
                disabled={!streamForm.linked_objective_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder={streamForm.linked_objective_id ? "Select initiative" : "Select objective first"} />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const selectedObjective = framework?.objectives?.find(obj => obj.id === streamForm.linked_objective_id);
                    return selectedObjective?.initiatives?.map(initiative => (
                      <SelectItem key={initiative.id} value={initiative.id}>
                        {initiative.initiative_id} - {initiative.initiative_name}
                      </SelectItem>
                    )) || [];
                  })()}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <TooltipLabel htmlFor="stream-id" tooltip={strategicFrameworkTooltips.revenueStreams.streamId}>
                  Stream ID *
                </TooltipLabel>
                <Input
                  id="stream-id"
                  placeholder="e.g., REV-001"
                  value={streamForm.stream_id}
                  onChange={(e) => setStreamForm(prev => ({...prev, stream_id: e.target.value}))}
                  className="form-input"
                />
              </div>

              <div className="space-y-2">
                <TooltipLabel htmlFor="stream-type" tooltip={strategicFrameworkTooltips.revenueStreams.revenueType}>
                  Stream Type *
                </TooltipLabel>
                <Select 
                  value={streamForm.stream_type} 
                  onValueChange={(value) => setStreamForm(prev => ({...prev, stream_type: value}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select stream type" />
                  </SelectTrigger>
                  <SelectContent>
                    {standardStreamTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <TooltipLabel htmlFor="stream-name" tooltip={strategicFrameworkTooltips.revenueStreams.streamName}>
                Stream Name *
              </TooltipLabel>
              <Input
                id="stream-name"
                placeholder="e.g., Diagnostics Services"
                value={streamForm.stream_name}
                onChange={(e) => setStreamForm(prev => ({...prev, stream_name: e.target.value}))}
                className="form-input"
              />
            </div>

            <div className="space-y-2">
              <TooltipLabel htmlFor="stream-description" tooltip={strategicFrameworkTooltips.revenueStreams.description}>
                Description
              </TooltipLabel>
              <Textarea
                id="stream-description"
                placeholder="Description of the revenue stream..."
                value={streamForm.description}
                onChange={(e) => setStreamForm(prev => ({...prev, description: e.target.value}))}
                className="form-input"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <TooltipLabel htmlFor="stream-owner" tooltip={strategicFrameworkTooltips.initiatives.owner}>
                Owner
              </TooltipLabel>
              <Select 
                value={streamForm.ownerCustom ? "__custom" : (streamForm.owner || "")} 
                onValueChange={(val) => {
                  if (val === "__custom") {
                    setStreamForm(prev => ({...prev, owner: "", ownerCustom: true}));
                  } else {
                    setStreamForm(prev => ({...prev, owner: val, ownerCustom: false}));
                  }
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select team member" /></SelectTrigger>
                <SelectContent>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.name}>
                      {member.name} {member.role && `(${member.role})`}
                    </SelectItem>
                  ))}
                  <SelectItem value="__custom">+ Enter Custom Name</SelectItem>
                </SelectContent>
              </Select>
              {streamForm.ownerCustom && (
                <Input 
                  autoFocus
                  placeholder="Enter custom owner name"
                  value={streamForm.owner}
                  onChange={(e) => setStreamForm(prev => ({...prev, owner: e.target.value}))}
                  className="mt-2"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="stream-notes">Notes</Label>
              <Textarea
                id="stream-notes"
                placeholder="Additional notes or considerations..."
                value={streamForm.notes}
                onChange={(e) => setStreamForm(prev => ({...prev, notes: e.target.value}))}
                className="form-input"
                rows={2}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowStreamDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Create Revenue Stream
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? `Edit ${productForm.type === 'services' ? 'Service' : 'Product'}` : `Add New ${productForm.type === 'services' ? 'Service' : 'Product'}`}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? `Update ${productForm.type === 'services' ? 'service' : 'product'} information and link it to a revenue stream.` : `Add a new ${productForm.type === 'services' ? 'service' : 'product'} and link it to a revenue stream.`}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateProduct} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <TooltipLabel htmlFor="product-sku" tooltip={strategicFrameworkTooltips.products.productId}>
                  SKU *
                </TooltipLabel>
                <Input
                  id="product-sku"
                  placeholder="e.g., PRD-001"
                  value={productForm.sku}
                  onChange={(e) => setProductForm(prev => ({...prev, sku: e.target.value}))}
                  className="form-input"
                />
              </div>

              <div className="space-y-2">
                <TooltipLabel htmlFor="product-type" tooltip={strategicFrameworkTooltips.products.productType}>
                  Type *
                </TooltipLabel>
                <Select value={productForm.type} onValueChange={(value) => {
                  const newId = generateNextId(value === 'services' ? 'service' : 'product', products);
                  setProductForm(prev => ({...prev, type: value, category: "", sku: editingItem ? prev.sku : newId}));
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="products">Products</SelectItem>
                    <SelectItem value="services">Services</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <TooltipLabel htmlFor="product-name" tooltip={strategicFrameworkTooltips.products.productName}>
                {productForm.type === 'services' ? 'Service' : 'Product'} Name *
              </TooltipLabel>
              <Input
                id="product-name"
                placeholder={`e.g., ${productForm.type === 'services' ? 'Consulting Service' : 'Software License'}`}
                value={productForm.name}
                onChange={(e) => setProductForm(prev => ({...prev, name: e.target.value}))}
                className="form-input"
              />
            </div>

            <div className="space-y-2">
              <TooltipLabel htmlFor="product-description" tooltip={strategicFrameworkTooltips.products.productName}>
                Description
              </TooltipLabel>
              <Textarea
                id="product-description"
                placeholder="Detailed description..."
                value={productForm.description}
                onChange={(e) => setProductForm(prev => ({...prev, description: e.target.value}))}
                className="form-input"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="product-category">Category</Label>
                <Select value={productForm.category} onValueChange={(value) => {
                  setProductForm(prev => ({...prev, category: value}));
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder={`Select ${productForm.type === 'services' ? 'service' : 'product'} category`} />
                  </SelectTrigger>
                  <SelectContent>
                    {(productForm.type === 'services' ? standardServiceCategories : standardProductCategories).map(category => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="revenue-stream">Revenue Stream *</Label>
                <Select 
                  value={productForm.linked_revenue_stream_id} 
                  onValueChange={(value) => setProductForm(prev => ({...prev, linked_revenue_stream_id: value}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select revenue stream to link this product to" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAllRevenueStreams().map(stream => (
                      <SelectItem key={stream.id} value={stream.id}>
                        <div className="flex flex-col">
                          <span className="font-medium">{stream.stream.stream_name}</span>
                          <span className="text-xs text-slate-500">
                            → {stream.stream.initiative?.initiative_name || 'N/A'} 
                            → {stream.stream.objective?.objective_name || 'N/A'}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Base Price, Currency, and COGS % - 3 column grid */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <TooltipLabel htmlFor="base-price" tooltip={strategicFrameworkTooltips.products.unitPrice}>
                  Base Price *
                </TooltipLabel>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                    {getCurrencySymbol(productForm.currency || currentPlan?.currency || 'USD')}
                  </span>
                  <Input
                    id="base-price"
                    type="number"
                    placeholder="e.g., 99.99"
                    value={productForm.base_price}
                    onChange={(e) => setProductForm(prev => ({...prev, base_price: e.target.value}))}
                    className="pl-8"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <TooltipLabel htmlFor="currency" tooltip="Currency for this product/service price">
                  Currency *
                </TooltipLabel>
                <Select 
                  value={productForm.currency || currentPlan?.currency || 'USD'} 
                  onValueChange={(val) => setProductForm(prev => ({...prev, currency: val}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((curr) => (
                      <SelectItem key={curr.value} value={curr.value}>
                        {getCurrencySymbol(curr.value)} {curr.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <TooltipLabel htmlFor="cogs-percentage" tooltip={strategicFrameworkTooltips.products.cogs}>
                  COGS (%)
                </TooltipLabel>
                <Input
                  id="cogs-percentage"
                  type="number"
                  placeholder="e.g., 30"
                  value={productForm.cogs_percentage}
                  onChange={(e) => setProductForm(prev => ({...prev, cogs_percentage: e.target.value}))}
                  className="form-input"
                />
              </div>
            </div>

            {/* Owner Field for Products */}
            <div className="space-y-2">
              <TooltipLabel htmlFor="product-owner" tooltip={strategicFrameworkTooltips.initiatives.owner}>
                Product Owner
              </TooltipLabel>
              <Select 
                value={productForm.ownerCustom ? "__custom" : (productForm.owner || "")} 
                onValueChange={(val) => {
                  if (val === "__custom") {
                    setProductForm(prev => ({...prev, owner: "", ownerCustom: true}));
                  } else {
                    setProductForm(prev => ({...prev, owner: val, ownerCustom: false}));
                  }
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select team member" /></SelectTrigger>
                <SelectContent>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.name}>
                      {member.name} {member.role && `(${member.role})`}
                    </SelectItem>
                  ))}
                  <SelectItem value="__custom">+ Enter Custom Name</SelectItem>
                </SelectContent>
              </Select>
              {productForm.ownerCustom && (
                <Input 
                  autoFocus
                  placeholder="Enter custom owner name"
                  value={productForm.owner}
                  onChange={(e) => setProductForm(prev => ({...prev, owner: e.target.value}))}
                  className="mt-2"
                />
              )}
            </div>

            <div className="border rounded-lg p-4 bg-blue-50">
              <h4 className="font-medium text-blue-800 mb-3">Sales Growth Tracking</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <TooltipLabel htmlFor="growth-rate" tooltip={strategicFrameworkTooltips.sales.annualGrowthRate}>
                    Annual Sales Volume Growth (%)
                  </TooltipLabel>
                  <Input
                    id="growth-rate"
                    type="number"
                    placeholder="e.g., 15"
                    value={productForm.annual_sales_volume_growth}
                    onChange={(e) => setProductForm(prev => ({...prev, annual_sales_volume_growth: e.target.value}))}
                    className="form-input"
                  />
                </div>

                <div className="space-y-2">
                  <TooltipLabel htmlFor="base-units" tooltip={strategicFrameworkTooltips.products.unitsSold}>
                    Base Year Units
                  </TooltipLabel>
                  <Input
                    id="base-units"
                    type="number"
                    placeholder="e.g., 1000"
                    value={productForm.base_year_units}
                    onChange={(e) => setProductForm(prev => ({...prev, base_year_units: e.target.value}))}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Toggle for Per-Year Growth Rates */}
              <div className="flex items-center space-x-2 mt-3">
                <input
                  type="checkbox"
                  id="use-per-year"
                  checked={productForm.use_per_year_rates}
                  onChange={(e) => {
                    const usePerYear = e.target.checked;
                    setProductForm(prev => ({
                      ...prev, 
                      use_per_year_rates: usePerYear,
                      growth_rates_per_year: usePerYear ? {
                        2025: prev.annual_sales_volume_growth || 0,
                        2026: prev.annual_sales_volume_growth || 0,
                        2027: prev.annual_sales_volume_growth || 0,
                        2028: prev.annual_sales_volume_growth || 0,
                        2029: prev.annual_sales_volume_growth || 0
                      } : {}
                    }));
                  }}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="use-per-year" className="cursor-pointer text-sm">
                  Use different growth rate for each year
                </Label>
              </div>

              {/* Per-Year Growth Rate Table */}
              {productForm.use_per_year_rates && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-blue-700 mb-2">Annual Growth Rates by Year</p>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-blue-100">
                        <tr>
                          <th className="px-3 py-2 text-left text-blue-800">Year</th>
                          <th className="px-3 py-2 text-left text-blue-800">Growth Rate (%)</th>
                          <th className="px-3 py-2 text-right text-blue-800">Projected Units</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {[2025, 2026, 2027, 2028, 2029].map((year, index) => {
                          const baseUnits = parseInt(productForm.base_year_units) || 0;
                          let projectedUnits = baseUnits;
                          
                          // Calculate compounded units
                          for (let y = 2025; y <= year; y++) {
                            if (y > 2025) {
                              const rate = productForm.growth_rates_per_year?.[y] || 0;
                              projectedUnits = projectedUnits * (1 + rate / 100);
                            }
                          }
                          
                          return (
                            <tr key={year} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-medium">{year}</td>
                              <td className="px-3 py-2">
                                <Input
                                  type="number"
                                  value={productForm.growth_rates_per_year?.[year] || 0}
                                  onChange={(e) => {
                                    setProductForm(prev => ({
                                      ...prev,
                                      growth_rates_per_year: {
                                        ...prev.growth_rates_per_year,
                                        [year]: parseFloat(e.target.value) || 0
                                      }
                                    }));
                                  }}
                                  className="w-20 text-right"
                                  placeholder="0"
                                />
                              </td>
                              <td className="px-3 py-2 text-right text-gray-600">
                                {Math.round(projectedUnits).toLocaleString()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Growth compounds year-over-year. Example: 100 units → 115 (+15%) → 138 (+20%) → 154 (+12%)
                  </p>
                </div>
              )}

              {!productForm.use_per_year_rates && (
                <p className="text-xs text-blue-600 mt-2">
                  Sales forecasting will use this growth rate to project future units sold
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="launch-year">Launch Year</Label>
                <Input
                  id="launch-year"
                  type="number"
                  placeholder="e.g., 2025"
                  value={productForm.launch_year}
                  onChange={(e) => setProductForm(prev => ({...prev, launch_year: e.target.value}))}
                  className="form-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="end-year">End Year</Label>
                <Input
                  id="end-year"
                  type="number"
                  placeholder="e.g., 2030"
                  value={productForm.end_year}
                  onChange={(e) => setProductForm(prev => ({...prev, end_year: e.target.value}))}
                  className="form-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="active">Status</Label>
                <Select 
                  value={productForm.active.toString()} 
                  onValueChange={(value) => setProductForm(prev => ({...prev, active: value === 'true'}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowProductDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                {editingItem ? `Update ${productForm.type === 'services' ? 'Service' : 'Product'}` : `Add ${productForm.type === 'services' ? 'Service' : 'Product'}`}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default StrategicFramework;